export type TranslationKey = keyof typeof translations.en;

export const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.appointments': 'Appointments',
    'nav.invoices': 'Invoices',
    'nav.messages': 'Messages',
    'nav.documents': 'Documents',
    'nav.refills': 'Prescription Refills',
    'nav.pets': 'Pets',
    'nav.profile': 'Profile',
    'nav.logOut': 'Log Out',
    'nav.aiBooking': 'AI Booking',

    // Dashboard
    'dashboard.greeting.morning': 'Good morning',
    'dashboard.greeting.afternoon': 'Good afternoon',
    'dashboard.greeting.evening': 'Good evening',
    'dashboard.yourPets': 'Your Pets',
    'dashboard.noPets': 'No pets found. Contact your veterinary clinic to get started.',
    'dashboard.quickActions': 'Quick Actions',
    'dashboard.bookAppointment': 'Book Appointment',
    'dashboard.requestRefill': 'Request Refill',
    'dashboard.viewDocuments': 'View Documents',
    'dashboard.overview': 'Overview',
    'dashboard.appointments': 'Appointments',
    'dashboard.upcoming': 'Upcoming',
    'dashboard.forms': 'Forms',
    'dashboard.pending': 'Pending',
    'dashboard.outstandingBalance': 'Outstanding Balance',
    'dashboard.allCaughtUp': 'All caught up!',
    'dashboard.payNow': 'Pay Now',
    'dashboard.upToDate': 'Up to date',
    'dashboard.checkNeeded': 'Check needed',
    'dashboard.prescriptionsNeedAttention': 'prescriptions need attention',

    // Common buttons
    'btn.book': 'Book',
    'btn.submit': 'Submit',
    'btn.cancel': 'Cancel',
    'btn.confirm': 'Confirm',
    'btn.pay': 'Pay',
    'btn.send': 'Send',
    'btn.save': 'Save',
    'btn.close': 'Close',
    'btn.back': 'Back',
    'btn.next': 'Next',
    'btn.requestRefill': 'Request Refill',
    'btn.submitRefill': 'Submit Refill Request',
    'btn.refillAllDue': 'Refill All Due',
    'btn.backToDashboard': 'Back to Dashboard',

    // Login page
    'login.title': 'Petra',
    'login.subtitle': 'Client Portal',
    'login.signIn': 'Sign in to your account',
    'login.createAccount': 'Create your account',
    'login.resetPassword': 'Reset your password',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.confirmPassword': 'Confirm Password',
    'login.firstName': 'First Name',
    'login.lastName': 'Last Name',
    'login.phone': 'Phone',
    'login.signInBtn': 'Sign In',
    'login.signingIn': 'Signing in...',
    'login.createAccountBtn': 'Create Account',
    'login.creatingAccount': 'Creating account...',
    'login.forgotPassword': 'Forgot Password?',
    'login.sendResetLink': 'Send Reset Link',
    'login.sending': 'Sending...',
    'login.noAccount': "Don't have an account?",
    'login.register': 'Register',
    'login.hasAccount': 'Already have an account?',
    'login.rememberPassword': 'Remember your password?',
    'login.backToSignIn': 'Back to Sign In',
    'login.poweredBy': 'Powered by Petra Veterinary Platform',
    'login.resetInfo': "Enter your email address and we'll send you a link to reset your password.",
    'login.emailPlaceholder': 'you@example.com',
    'login.passwordPlaceholder': 'Enter your password',
    'login.passwordMinLength': 'At least 8 characters',
    'login.confirmPlaceholder': 'Confirm your password',

    // Refills page
    'refills.title': 'Prescription Refills',
    'refills.subtitle': 'Request medication refills for your pets',
    'refills.newRequest': 'New Refill Request',
    'refills.quickSelect': 'Quick Select from Active Prescriptions',
    'refills.orManually': 'or enter manually',
    'refills.pet': 'Pet',
    'refills.selectPet': 'Select a pet...',
    'refills.medication': 'Medication Name',
    'refills.medicationPlaceholder': 'e.g. Apoquel, Rimadyl, Heartgard...',
    'refills.dosage': 'Dosage',
    'refills.dosageOptional': 'optional',
    'refills.dosagePlaceholder': 'e.g. 16mg twice daily',
    'refills.notes': 'Notes',
    'refills.notesOptional': 'optional',
    'refills.notesPlaceholder': 'Any additional details for your vet...',
    'refills.submitting': 'Submitting...',
    'refills.success': 'Refill request submitted! Your clinic will review it shortly.',
    'refills.prescriptionHistory': 'Prescription History',
    'refills.refillRequests': 'Refill Requests',
    'refills.noRequests': 'No refill requests yet',
    'refills.noRequestsDesc': "Request a prescription refill for your pet's medication.",
    'refills.requestARefill': 'Request a Refill',
    'refills.refillsRemaining': 'refills remaining',
    'refills.noRefillsLeft': 'No refills left',
    'refills.dueForRefill': 'prescriptions due for refill',
    'refills.confirmBatch': 'This will request refills for:',
    'refills.batchSuccess': 'All refill requests submitted successfully!',
    'refills.prescriptions': "'s Prescriptions",

    // Refill alerts
    'refillAlert.lastRefill': 'Last refill!',
    'refillAlert.noRefills': '0 refills remaining — contact vet',
    'refillAlert.remaining': 'refills remaining',

    // Scheduling agent
    'agent.title': 'AI Booking Assistant',
    'agent.subtitle': 'Book, reschedule, or cancel',
    'agent.greeting': "Hi! I can help you book, reschedule, or cancel appointments. Try saying 'Book [pet name] a checkup next week'",
    'agent.whatToDo': 'What would you like to do?',
    'agent.bookAppointment': 'Book appointment',
    'agent.reschedule': 'Reschedule',
    'agent.cancelAppointment': 'Cancel appointment',
    'agent.placeholder': 'Type your request...',
    'agent.error': "I'm sorry, something went wrong. Please try again or use the appointment booking page.",

    // Statuses
    'status.approved': 'Approved',
    'status.filled': 'Filled',
    'status.pending': 'Pending',
    'status.denied': 'Denied',
    'status.active': 'ACTIVE',
    'status.expired': 'EXPIRED',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Something went wrong. Please try again.',
    'common.required': 'required',
    'common.optional': 'optional',

    // Lab results
    'lab.status.normal': 'Normal',
    'lab.status.high': 'High',
    'lab.status.low': 'Low',
    'lab.status.critical': 'Critical',
    'lab.noResults': 'No lab results available.',
    'lab.abnormal': 'abnormal',
    'lab.summary': 'Summary',
    'lab.testName': 'Test Name',
    'lab.value': 'Value',
    'lab.units': 'Units',
    'lab.referenceRange': 'Reference Range',
    'lab.status': 'Status',
    'lab.trend': 'Trend',
    'lab.showTrend': 'Chart',
    'lab.hideTrend': 'Hide',
    'lab.downloadPdf': 'Download PDF',
    'lab.trendTitle': 'Trend',
    'lab.normalRange': 'Normal Range',
    'lab.labResults': 'Lab Results',

    // Lab panel names
    'lab.panel.cbc': 'Complete Blood Count',
    'lab.panel.chemistry': 'Chemistry Panel',
    'lab.panel.urinalysis': 'Urinalysis',
    'lab.panel.thyroid': 'Thyroid Panel',
    'lab.panel.electrolyte': 'Electrolyte Panel',
    'lab.panel.coagulation': 'Coagulation Panel',

    // Document types
    'doc.type.labResults': 'Lab Results',
    'doc.type.vaccinationCertificate': 'Vaccination Certificate',
    'doc.type.dischargeInstructions': 'Discharge Instructions',
    'doc.type.receipt': 'Receipt',
    'doc.type.other': 'Other',
    'doc.type.allTypes': 'All Types',
    'doc.filter': 'Filter',
    'doc.clearFilters': 'Clear filters',
    'doc.documentType': 'Document Type',
    'doc.pet': 'Pet',
    'doc.allPets': 'All Pets',
    'doc.noDocuments': 'No documents available yet.',
    'doc.noMatch': 'No documents match your filters.',
    'doc.title': 'Documents',
    'doc.subtitle': 'View and download your records',
  },
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.appointments': 'Citas',
    'nav.invoices': 'Facturas',
    'nav.messages': 'Mensajes',
    'nav.documents': 'Documentos',
    'nav.refills': 'Recetas M\u00e9dicas',
    'nav.pets': 'Mascotas',
    'nav.profile': 'Perfil',
    'nav.logOut': 'Cerrar Sesi\u00f3n',
    'nav.aiBooking': 'Asistente IA',

    // Dashboard
    'dashboard.greeting.morning': 'Buenos d\u00edas',
    'dashboard.greeting.afternoon': 'Buenas tardes',
    'dashboard.greeting.evening': 'Buenas noches',
    'dashboard.yourPets': 'Tus Mascotas',
    'dashboard.noPets': 'No se encontraron mascotas. Contacta a tu cl\u00ednica veterinaria para comenzar.',
    'dashboard.quickActions': 'Acciones R\u00e1pidas',
    'dashboard.bookAppointment': 'Reservar Cita',
    'dashboard.requestRefill': 'Solicitar Receta',
    'dashboard.viewDocuments': 'Ver Documentos',
    'dashboard.overview': 'Resumen',
    'dashboard.appointments': 'Citas',
    'dashboard.upcoming': 'Pr\u00f3ximas',
    'dashboard.forms': 'Formularios',
    'dashboard.pending': 'Pendientes',
    'dashboard.outstandingBalance': 'Saldo Pendiente',
    'dashboard.allCaughtUp': '\u00a1Todo al d\u00eda!',
    'dashboard.payNow': 'Pagar Ahora',
    'dashboard.upToDate': 'Al d\u00eda',
    'dashboard.checkNeeded': 'Revisi\u00f3n necesaria',
    'dashboard.prescriptionsNeedAttention': 'recetas necesitan atenci\u00f3n',

    // Common buttons
    'btn.book': 'Reservar',
    'btn.submit': 'Enviar',
    'btn.cancel': 'Cancelar',
    'btn.confirm': 'Confirmar',
    'btn.pay': 'Pagar',
    'btn.send': 'Enviar',
    'btn.save': 'Guardar',
    'btn.close': 'Cerrar',
    'btn.back': 'Atr\u00e1s',
    'btn.next': 'Siguiente',
    'btn.requestRefill': 'Solicitar Receta',
    'btn.submitRefill': 'Enviar Solicitud de Receta',
    'btn.refillAllDue': 'Rellenar Todas las Pendientes',
    'btn.backToDashboard': 'Volver al Panel',

    // Login page
    'login.title': 'Petra',
    'login.subtitle': 'Portal del Cliente',
    'login.signIn': 'Inicia sesi\u00f3n en tu cuenta',
    'login.createAccount': 'Crea tu cuenta',
    'login.resetPassword': 'Restablecer tu contrase\u00f1a',
    'login.email': 'Correo electr\u00f3nico',
    'login.password': 'Contrase\u00f1a',
    'login.confirmPassword': 'Confirmar Contrase\u00f1a',
    'login.firstName': 'Nombre',
    'login.lastName': 'Apellido',
    'login.phone': 'Tel\u00e9fono',
    'login.signInBtn': 'Iniciar Sesi\u00f3n',
    'login.signingIn': 'Iniciando sesi\u00f3n...',
    'login.createAccountBtn': 'Crear Cuenta',
    'login.creatingAccount': 'Creando cuenta...',
    'login.forgotPassword': '\u00bfOlvidaste tu contrase\u00f1a?',
    'login.sendResetLink': 'Enviar Enlace',
    'login.sending': 'Enviando...',
    'login.noAccount': '\u00bfNo tienes una cuenta?',
    'login.register': 'Reg\u00edstrate',
    'login.hasAccount': '\u00bfYa tienes una cuenta?',
    'login.rememberPassword': '\u00bfRecuerdas tu contrase\u00f1a?',
    'login.backToSignIn': 'Volver a Iniciar Sesi\u00f3n',
    'login.poweredBy': 'Impulsado por Petra Veterinary Platform',
    'login.resetInfo': 'Ingresa tu correo electr\u00f3nico y te enviaremos un enlace para restablecer tu contrase\u00f1a.',
    'login.emailPlaceholder': 'tu@ejemplo.com',
    'login.passwordPlaceholder': 'Ingresa tu contrase\u00f1a',
    'login.passwordMinLength': 'Al menos 8 caracteres',
    'login.confirmPlaceholder': 'Confirma tu contrase\u00f1a',

    // Refills page
    'refills.title': 'Recetas M\u00e9dicas',
    'refills.subtitle': 'Solicita recargas de medicamentos para tus mascotas',
    'refills.newRequest': 'Nueva Solicitud de Recarga',
    'refills.quickSelect': 'Selecci\u00f3n R\u00e1pida de Recetas Activas',
    'refills.orManually': 'o ingresa manualmente',
    'refills.pet': 'Mascota',
    'refills.selectPet': 'Selecciona una mascota...',
    'refills.medication': 'Nombre del Medicamento',
    'refills.medicationPlaceholder': 'ej. Apoquel, Rimadyl, Heartgard...',
    'refills.dosage': 'Dosis',
    'refills.dosageOptional': 'opcional',
    'refills.dosagePlaceholder': 'ej. 16mg dos veces al d\u00eda',
    'refills.notes': 'Notas',
    'refills.notesOptional': 'opcional',
    'refills.notesPlaceholder': 'Detalles adicionales para tu veterinario...',
    'refills.submitting': 'Enviando...',
    'refills.success': '\u00a1Solicitud de recarga enviada! Tu cl\u00ednica la revisar\u00e1 pronto.',
    'refills.prescriptionHistory': 'Historial de Recetas',
    'refills.refillRequests': 'Solicitudes de Recarga',
    'refills.noRequests': 'A\u00fan no hay solicitudes de recarga',
    'refills.noRequestsDesc': 'Solicita una recarga de medicamento para tu mascota.',
    'refills.requestARefill': 'Solicitar una Recarga',
    'refills.refillsRemaining': 'recargas restantes',
    'refills.noRefillsLeft': 'Sin recargas disponibles',
    'refills.dueForRefill': 'recetas pendientes de recarga',
    'refills.confirmBatch': 'Esto solicitar\u00e1 recargas para:',
    'refills.batchSuccess': '\u00a1Todas las solicitudes de recarga enviadas exitosamente!',
    'refills.prescriptions': ' - Recetas',

    // Refill alerts
    'refillAlert.lastRefill': '\u00a1\u00daltima recarga!',
    'refillAlert.noRefills': '0 recargas restantes \u2014 contacta al veterinario',
    'refillAlert.remaining': 'recargas restantes',

    // Scheduling agent
    'agent.title': 'Asistente de Reservas IA',
    'agent.subtitle': 'Reservar, reprogramar o cancelar',
    'agent.greeting': '\u00a1Hola! Puedo ayudarte a reservar, reprogramar o cancelar citas. Prueba diciendo "Reservar una revisi\u00f3n para [nombre de mascota] la pr\u00f3xima semana"',
    'agent.whatToDo': '\u00bfQu\u00e9 te gustar\u00eda hacer?',
    'agent.bookAppointment': 'Reservar cita',
    'agent.reschedule': 'Reprogramar',
    'agent.cancelAppointment': 'Cancelar cita',
    'agent.placeholder': 'Escribe tu solicitud...',
    'agent.error': 'Lo siento, algo sali\u00f3 mal. Int\u00e9ntalo de nuevo o usa la p\u00e1gina de reserva de citas.',

    // Statuses
    'status.approved': 'Aprobado',
    'status.filled': 'Surtido',
    'status.pending': 'Pendiente',
    'status.denied': 'Denegado',
    'status.active': 'ACTIVO',
    'status.expired': 'EXPIRADO',

    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Algo sali\u00f3 mal. Int\u00e9ntalo de nuevo.',
    'common.required': 'requerido',
    'common.optional': 'opcional',

    // Lab results
    'lab.status.normal': 'Normal',
    'lab.status.high': 'Alto',
    'lab.status.low': 'Bajo',
    'lab.status.critical': 'Cr\u00edtico',
    'lab.noResults': 'No hay resultados de laboratorio disponibles.',
    'lab.abnormal': 'anormal',
    'lab.summary': 'Resumen',
    'lab.testName': 'Nombre de Prueba',
    'lab.value': 'Valor',
    'lab.units': 'Unidades',
    'lab.referenceRange': 'Rango de Referencia',
    'lab.status': 'Estado',
    'lab.trend': 'Tendencia',
    'lab.showTrend': 'Gr\u00e1fico',
    'lab.hideTrend': 'Ocultar',
    'lab.downloadPdf': 'Descargar PDF',
    'lab.trendTitle': 'Tendencia',
    'lab.normalRange': 'Rango Normal',
    'lab.labResults': 'Resultados de Laboratorio',

    // Lab panel names
    'lab.panel.cbc': 'Hemograma Completo',
    'lab.panel.chemistry': 'Panel de Qu\u00edmica',
    'lab.panel.urinalysis': 'An\u00e1lisis de Orina',
    'lab.panel.thyroid': 'Panel Tiroideo',
    'lab.panel.electrolyte': 'Panel de Electrolitos',
    'lab.panel.coagulation': 'Panel de Coagulaci\u00f3n',

    // Document types
    'doc.type.labResults': 'Resultados de Laboratorio',
    'doc.type.vaccinationCertificate': 'Certificado de Vacunaci\u00f3n',
    'doc.type.dischargeInstructions': 'Instrucciones de Alta',
    'doc.type.receipt': 'Recibo',
    'doc.type.other': 'Otro',
    'doc.type.allTypes': 'Todos los Tipos',
    'doc.filter': 'Filtrar',
    'doc.clearFilters': 'Limpiar filtros',
    'doc.documentType': 'Tipo de Documento',
    'doc.pet': 'Mascota',
    'doc.allPets': 'Todas las Mascotas',
    'doc.noDocuments': 'A\u00fan no hay documentos disponibles.',
    'doc.noMatch': 'Ning\u00fan documento coincide con tus filtros.',
    'doc.title': 'Documentos',
    'doc.subtitle': 'Ver y descargar tus registros',
  },
} as const;

export const DEFAULT_LOCALE: Locale = 'en';

// ─── UI Translations (i18n) ─────────────────────────────────────
// Static UI string translations for the client portal.
// Used for navigation labels, buttons, form labels, status badges.
// For dynamic content translation (discharge instructions, SMS), see src/lib/translation.ts.

export type Locale = 'en' | 'es' | 'vi';

export const SUPPORTED_LOCALES: Locale[] = ['en', 'es', 'vi'];

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  vi: 'Tiếng Việt',
};

// ─── Translation Keys ────────────────────────────────────────────

export interface TranslationStrings {
  // Navigation
  'nav.home': string;
  'nav.appointments': string;
  'nav.invoices': string;
  'nav.messages': string;
  'nav.documents': string;
  'nav.profile': string;
  'nav.pets': string;
  'nav.boarding': string;
  'nav.prescriptions': string;
  'nav.logout': string;

  // Common buttons
  'btn.book': string;
  'btn.send': string;
  'btn.cancel': string;
  'btn.confirm': string;
  'btn.pay': string;
  'btn.save': string;
  'btn.edit': string;
  'btn.delete': string;
  'btn.back': string;
  'btn.next': string;
  'btn.submit': string;
  'btn.close': string;
  'btn.tryAgain': string;
  'btn.viewAll': string;
  'btn.download': string;

  // Dashboard
  'dashboard.welcome': string;
  'dashboard.upcomingAppointments': string;
  'dashboard.recentInvoices': string;
  'dashboard.myPets': string;
  'dashboard.noUpcoming': string;
  'dashboard.noPets': string;
  'dashboard.quickActions': string;

  // Appointments
  'appointments.title': string;
  'appointments.upcoming': string;
  'appointments.past': string;
  'appointments.noAppointments': string;
  'appointments.bookNew': string;
  'appointments.date': string;
  'appointments.time': string;
  'appointments.doctor': string;
  'appointments.type': string;
  'appointments.reason': string;
  'appointments.cancelAppointment': string;
  'appointments.confirmCancel': string;

  // Invoices / Billing
  'invoices.title': string;
  'invoices.outstanding': string;
  'invoices.paid': string;
  'invoices.amount': string;
  'invoices.dueDate': string;
  'invoices.payNow': string;
  'invoices.paymentHistory': string;
  'invoices.noInvoices': string;
  'invoices.total': string;

  // Messages
  'messages.title': string;
  'messages.newMessage': string;
  'messages.noMessages': string;
  'messages.typeMessage': string;
  'messages.sentBy': string;

  // Pets
  'pets.title': string;
  'pets.species': string;
  'pets.breed': string;
  'pets.age': string;
  'pets.weight': string;
  'pets.sex': string;
  'pets.microchip': string;
  'pets.insurance': string;
  'pets.noPets': string;
  'pets.healthAlerts': string;
  'pets.weightHistory': string;
  'pets.activeProblems': string;

  // Forms
  'form.required': string;
  'form.email': string;
  'form.phone': string;
  'form.name': string;
  'form.date': string;
  'form.time': string;
  'form.notes': string;
  'form.selectOption': string;

  // Status badges
  'status.scheduled': string;
  'status.confirmed': string;
  'status.completed': string;
  'status.cancelled': string;
  'status.pending': string;
  'status.paid': string;
  'status.overdue': string;
  'status.active': string;
  'status.inactive': string;

  // Boarding
  'boarding.title': string;
  'boarding.dailyReports': string;
  'boarding.appetite': string;
  'boarding.behavior': string;
  'boarding.exercise': string;
  'boarding.medications': string;
  'boarding.notes': string;
  'boarding.photos': string;
  'boarding.noReports': string;

  // Profile
  'profile.title': string;
  'profile.personalInfo': string;
  'profile.preferences': string;
  'profile.language': string;
  'profile.notifications': string;
  'profile.changePassword': string;

  // General
  'general.loading': string;
  'general.error': string;
  'general.noResults': string;
  'general.search': string;
  'general.filter': string;
  'general.sort': string;
}

// ─── English (en) ────────────────────────────────────────────────

const en: TranslationStrings = {
  // Navigation
  'nav.home': 'Home',
  'nav.appointments': 'Appointments',
  'nav.invoices': 'Invoices',
  'nav.messages': 'Messages',
  'nav.documents': 'Documents',
  'nav.profile': 'Profile',
  'nav.pets': 'My Pets',
  'nav.boarding': 'Boarding',
  'nav.prescriptions': 'Prescriptions',
  'nav.logout': 'Log Out',

  // Common buttons
  'btn.book': 'Book Appointment',
  'btn.send': 'Send',
  'btn.cancel': 'Cancel',
  'btn.confirm': 'Confirm',
  'btn.pay': 'Pay Now',
  'btn.save': 'Save',
  'btn.edit': 'Edit',
  'btn.delete': 'Delete',
  'btn.back': 'Back',
  'btn.next': 'Next',
  'btn.submit': 'Submit',
  'btn.close': 'Close',
  'btn.tryAgain': 'Try Again',
  'btn.viewAll': 'View All',
  'btn.download': 'Download',

  // Dashboard
  'dashboard.welcome': 'Welcome back',
  'dashboard.upcomingAppointments': 'Upcoming Appointments',
  'dashboard.recentInvoices': 'Recent Invoices',
  'dashboard.myPets': 'My Pets',
  'dashboard.noUpcoming': 'No upcoming appointments',
  'dashboard.noPets': 'No pets found',
  'dashboard.quickActions': 'Quick Actions',

  // Appointments
  'appointments.title': 'Appointments',
  'appointments.upcoming': 'Upcoming',
  'appointments.past': 'Past',
  'appointments.noAppointments': 'No appointments scheduled',
  'appointments.bookNew': 'Book New Appointment',
  'appointments.date': 'Date',
  'appointments.time': 'Time',
  'appointments.doctor': 'Doctor',
  'appointments.type': 'Type',
  'appointments.reason': 'Reason for Visit',
  'appointments.cancelAppointment': 'Cancel Appointment',
  'appointments.confirmCancel': 'Are you sure you want to cancel this appointment?',

  // Invoices / Billing
  'invoices.title': 'Invoices',
  'invoices.outstanding': 'Outstanding',
  'invoices.paid': 'Paid',
  'invoices.amount': 'Amount',
  'invoices.dueDate': 'Due Date',
  'invoices.payNow': 'Pay Now',
  'invoices.paymentHistory': 'Payment History',
  'invoices.noInvoices': 'No invoices found',
  'invoices.total': 'Total',

  // Messages
  'messages.title': 'Messages',
  'messages.newMessage': 'New Message',
  'messages.noMessages': 'No messages yet',
  'messages.typeMessage': 'Type your message...',
  'messages.sentBy': 'Sent by',

  // Pets
  'pets.title': 'My Pets',
  'pets.species': 'Species',
  'pets.breed': 'Breed',
  'pets.age': 'Age',
  'pets.weight': 'Weight',
  'pets.sex': 'Sex',
  'pets.microchip': 'Microchip',
  'pets.insurance': 'Insurance',
  'pets.noPets': 'No pets found',
  'pets.healthAlerts': 'Health Alerts',
  'pets.weightHistory': 'Weight History',
  'pets.activeProblems': 'Active Problems',

  // Forms
  'form.required': 'Required',
  'form.email': 'Email',
  'form.phone': 'Phone',
  'form.name': 'Name',
  'form.date': 'Date',
  'form.time': 'Time',
  'form.notes': 'Notes',
  'form.selectOption': 'Select an option',

  // Status badges
  'status.scheduled': 'Scheduled',
  'status.confirmed': 'Confirmed',
  'status.completed': 'Completed',
  'status.cancelled': 'Cancelled',
  'status.pending': 'Pending',
  'status.paid': 'Paid',
  'status.overdue': 'Overdue',
  'status.active': 'Active',
  'status.inactive': 'Inactive',

  // Boarding
  'boarding.title': 'Boarding',
  'boarding.dailyReports': 'Daily Reports',
  'boarding.appetite': 'Appetite',
  'boarding.behavior': 'Behavior',
  'boarding.exercise': 'Exercise',
  'boarding.medications': 'Medications',
  'boarding.notes': 'Staff Notes',
  'boarding.photos': 'Photos & Videos',
  'boarding.noReports': 'No boarding reports yet',

  // Profile
  'profile.title': 'Profile',
  'profile.personalInfo': 'Personal Information',
  'profile.preferences': 'Preferences',
  'profile.language': 'Language',
  'profile.notifications': 'Notifications',
  'profile.changePassword': 'Change Password',

  // General
  'general.loading': 'Loading...',
  'general.error': 'Something went wrong',
  'general.noResults': 'No results found',
  'general.search': 'Search',
  'general.filter': 'Filter',
  'general.sort': 'Sort',
};

// ─── Spanish (es) ────────────────────────────────────────────────

const es: TranslationStrings = {
  // Navigation
  'nav.home': 'Inicio',
  'nav.appointments': 'Citas',
  'nav.invoices': 'Facturas',
  'nav.messages': 'Mensajes',
  'nav.documents': 'Documentos',
  'nav.profile': 'Perfil',
  'nav.pets': 'Mis Mascotas',
  'nav.boarding': 'Hospedaje',
  'nav.prescriptions': 'Recetas',
  'nav.logout': 'Cerrar Sesión',

  // Common buttons
  'btn.book': 'Reservar Cita',
  'btn.send': 'Enviar',
  'btn.cancel': 'Cancelar',
  'btn.confirm': 'Confirmar',
  'btn.pay': 'Pagar Ahora',
  'btn.save': 'Guardar',
  'btn.edit': 'Editar',
  'btn.delete': 'Eliminar',
  'btn.back': 'Volver',
  'btn.next': 'Siguiente',
  'btn.submit': 'Enviar',
  'btn.close': 'Cerrar',
  'btn.tryAgain': 'Intentar de Nuevo',
  'btn.viewAll': 'Ver Todo',
  'btn.download': 'Descargar',

  // Dashboard
  'dashboard.welcome': 'Bienvenido de nuevo',
  'dashboard.upcomingAppointments': 'Próximas Citas',
  'dashboard.recentInvoices': 'Facturas Recientes',
  'dashboard.myPets': 'Mis Mascotas',
  'dashboard.noUpcoming': 'No hay citas próximas',
  'dashboard.noPets': 'No se encontraron mascotas',
  'dashboard.quickActions': 'Acciones Rápidas',

  // Appointments
  'appointments.title': 'Citas',
  'appointments.upcoming': 'Próximas',
  'appointments.past': 'Pasadas',
  'appointments.noAppointments': 'No hay citas programadas',
  'appointments.bookNew': 'Reservar Nueva Cita',
  'appointments.date': 'Fecha',
  'appointments.time': 'Hora',
  'appointments.doctor': 'Doctor',
  'appointments.type': 'Tipo',
  'appointments.reason': 'Motivo de la Visita',
  'appointments.cancelAppointment': 'Cancelar Cita',
  'appointments.confirmCancel': '¿Está seguro de que desea cancelar esta cita?',

  // Invoices / Billing
  'invoices.title': 'Facturas',
  'invoices.outstanding': 'Pendientes',
  'invoices.paid': 'Pagadas',
  'invoices.amount': 'Monto',
  'invoices.dueDate': 'Fecha de Vencimiento',
  'invoices.payNow': 'Pagar Ahora',
  'invoices.paymentHistory': 'Historial de Pagos',
  'invoices.noInvoices': 'No se encontraron facturas',
  'invoices.total': 'Total',

  // Messages
  'messages.title': 'Mensajes',
  'messages.newMessage': 'Nuevo Mensaje',
  'messages.noMessages': 'Aún no hay mensajes',
  'messages.typeMessage': 'Escribe tu mensaje...',
  'messages.sentBy': 'Enviado por',

  // Pets
  'pets.title': 'Mis Mascotas',
  'pets.species': 'Especie',
  'pets.breed': 'Raza',
  'pets.age': 'Edad',
  'pets.weight': 'Peso',
  'pets.sex': 'Sexo',
  'pets.microchip': 'Microchip',
  'pets.insurance': 'Seguro',
  'pets.noPets': 'No se encontraron mascotas',
  'pets.healthAlerts': 'Alertas de Salud',
  'pets.weightHistory': 'Historial de Peso',
  'pets.activeProblems': 'Problemas Activos',

  // Forms
  'form.required': 'Obligatorio',
  'form.email': 'Correo electrónico',
  'form.phone': 'Teléfono',
  'form.name': 'Nombre',
  'form.date': 'Fecha',
  'form.time': 'Hora',
  'form.notes': 'Notas',
  'form.selectOption': 'Seleccione una opción',

  // Status badges
  'status.scheduled': 'Programada',
  'status.confirmed': 'Confirmada',
  'status.completed': 'Completada',
  'status.cancelled': 'Cancelada',
  'status.pending': 'Pendiente',
  'status.paid': 'Pagada',
  'status.overdue': 'Vencida',
  'status.active': 'Activo',
  'status.inactive': 'Inactivo',

  // Boarding
  'boarding.title': 'Hospedaje',
  'boarding.dailyReports': 'Reportes Diarios',
  'boarding.appetite': 'Apetito',
  'boarding.behavior': 'Comportamiento',
  'boarding.exercise': 'Ejercicio',
  'boarding.medications': 'Medicamentos',
  'boarding.notes': 'Notas del Personal',
  'boarding.photos': 'Fotos y Videos',
  'boarding.noReports': 'Aún no hay reportes de hospedaje',

  // Profile
  'profile.title': 'Perfil',
  'profile.personalInfo': 'Información Personal',
  'profile.preferences': 'Preferencias',
  'profile.language': 'Idioma',
  'profile.notifications': 'Notificaciones',
  'profile.changePassword': 'Cambiar Contraseña',

  // General
  'general.loading': 'Cargando...',
  'general.error': 'Algo salió mal',
  'general.noResults': 'No se encontraron resultados',
  'general.search': 'Buscar',
  'general.filter': 'Filtrar',
  'general.sort': 'Ordenar',
};

// ─── Vietnamese (vi) ─────────────────────────────────────────────

const vi: TranslationStrings = {
  // Navigation
  'nav.home': 'Trang chủ',
  'nav.appointments': 'Lịch hẹn',
  'nav.invoices': 'Hóa đơn',
  'nav.messages': 'Tin nhắn',
  'nav.documents': 'Tài liệu',
  'nav.profile': 'Hồ sơ',
  'nav.pets': 'Thú cưng của tôi',
  'nav.boarding': 'Lưu trú',
  'nav.prescriptions': 'Đơn thuốc',
  'nav.logout': 'Đăng xuất',

  // Common buttons
  'btn.book': 'Đặt lịch',
  'btn.send': 'Gửi',
  'btn.cancel': 'Hủy',
  'btn.confirm': 'Xác nhận',
  'btn.pay': 'Thanh toán',
  'btn.save': 'Lưu',
  'btn.edit': 'Chỉnh sửa',
  'btn.delete': 'Xóa',
  'btn.back': 'Quay lại',
  'btn.next': 'Tiếp theo',
  'btn.submit': 'Gửi đi',
  'btn.close': 'Đóng',
  'btn.tryAgain': 'Thử lại',
  'btn.viewAll': 'Xem tất cả',
  'btn.download': 'Tải xuống',

  // Dashboard
  'dashboard.welcome': 'Chào mừng trở lại',
  'dashboard.upcomingAppointments': 'Lịch hẹn sắp tới',
  'dashboard.recentInvoices': 'Hóa đơn gần đây',
  'dashboard.myPets': 'Thú cưng của tôi',
  'dashboard.noUpcoming': 'Không có lịch hẹn sắp tới',
  'dashboard.noPets': 'Không tìm thấy thú cưng',
  'dashboard.quickActions': 'Thao tác nhanh',

  // Appointments
  'appointments.title': 'Lịch hẹn',
  'appointments.upcoming': 'Sắp tới',
  'appointments.past': 'Đã qua',
  'appointments.noAppointments': 'Không có lịch hẹn nào',
  'appointments.bookNew': 'Đặt lịch hẹn mới',
  'appointments.date': 'Ngày',
  'appointments.time': 'Giờ',
  'appointments.doctor': 'Bác sĩ',
  'appointments.type': 'Loại',
  'appointments.reason': 'Lý do khám',
  'appointments.cancelAppointment': 'Hủy lịch hẹn',
  'appointments.confirmCancel': 'Bạn có chắc chắn muốn hủy lịch hẹn này không?',

  // Invoices / Billing
  'invoices.title': 'Hóa đơn',
  'invoices.outstanding': 'Chưa thanh toán',
  'invoices.paid': 'Đã thanh toán',
  'invoices.amount': 'Số tiền',
  'invoices.dueDate': 'Ngày đến hạn',
  'invoices.payNow': 'Thanh toán ngay',
  'invoices.paymentHistory': 'Lịch sử thanh toán',
  'invoices.noInvoices': 'Không tìm thấy hóa đơn',
  'invoices.total': 'Tổng cộng',

  // Messages
  'messages.title': 'Tin nhắn',
  'messages.newMessage': 'Tin nhắn mới',
  'messages.noMessages': 'Chưa có tin nhắn',
  'messages.typeMessage': 'Nhập tin nhắn của bạn...',
  'messages.sentBy': 'Gửi bởi',

  // Pets
  'pets.title': 'Thú cưng của tôi',
  'pets.species': 'Loài',
  'pets.breed': 'Giống',
  'pets.age': 'Tuổi',
  'pets.weight': 'Cân nặng',
  'pets.sex': 'Giới tính',
  'pets.microchip': 'Vi mạch',
  'pets.insurance': 'Bảo hiểm',
  'pets.noPets': 'Không tìm thấy thú cưng',
  'pets.healthAlerts': 'Cảnh báo sức khỏe',
  'pets.weightHistory': 'Lịch sử cân nặng',
  'pets.activeProblems': 'Vấn đề đang hoạt động',

  // Forms
  'form.required': 'Bắt buộc',
  'form.email': 'Email',
  'form.phone': 'Điện thoại',
  'form.name': 'Tên',
  'form.date': 'Ngày',
  'form.time': 'Giờ',
  'form.notes': 'Ghi chú',
  'form.selectOption': 'Chọn một tùy chọn',

  // Status badges
  'status.scheduled': 'Đã lên lịch',
  'status.confirmed': 'Đã xác nhận',
  'status.completed': 'Hoàn thành',
  'status.cancelled': 'Đã hủy',
  'status.pending': 'Đang chờ',
  'status.paid': 'Đã thanh toán',
  'status.overdue': 'Quá hạn',
  'status.active': 'Đang hoạt động',
  'status.inactive': 'Không hoạt động',

  // Boarding
  'boarding.title': 'Lưu trú',
  'boarding.dailyReports': 'Báo cáo hàng ngày',
  'boarding.appetite': 'Sự thèm ăn',
  'boarding.behavior': 'Hành vi',
  'boarding.exercise': 'Vận động',
  'boarding.medications': 'Thuốc',
  'boarding.notes': 'Ghi chú nhân viên',
  'boarding.photos': 'Ảnh và Video',
  'boarding.noReports': 'Chưa có báo cáo lưu trú',

  // Profile
  'profile.title': 'Hồ sơ',
  'profile.personalInfo': 'Thông tin cá nhân',
  'profile.preferences': 'Tùy chọn',
  'profile.language': 'Ngôn ngữ',
  'profile.notifications': 'Thông báo',
  'profile.changePassword': 'Đổi mật khẩu',

  // General
  'general.loading': 'Đang tải...',
  'general.error': 'Đã xảy ra lỗi',
  'general.noResults': 'Không tìm thấy kết quả',
  'general.search': 'Tìm kiếm',
  'general.filter': 'Lọc',
  'general.sort': 'Sắp xếp',
};

// ─── Translation Map ─────────────────────────────────────────────

const translationMap: Record<Locale, TranslationStrings> = { en, es, vi };

/**
 * Get a translated string for the given locale and key.
 * Falls back to English if the locale or key is not found.
 */
export function t(locale: Locale, key: keyof TranslationStrings): string {
  const strings = translationMap[locale] || translationMap.en;
  return strings[key] || translationMap.en[key] || key;
}

/**
 * Get all translations for a locale.
 */
export function getTranslations(locale: Locale): TranslationStrings {
  return translationMap[locale] || translationMap.en;
}

/**
 * Check if a locale is supported.
 */
export function isValidLocale(code: string): code is Locale {
  return SUPPORTED_LOCALES.includes(code as Locale);
}

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Espa\u00f1ol",
  vi: "Ti\u1ebfng Vi\u1ec7t",
};

// ── Scheduling Agent Translations ──────────────────────────────

export const SCHEDULING_AGENT_TRANSLATIONS: Record<
  Locale,
  {
    greeting: string;
    subtitle: string;
    quickActions: string[];
    placeholder: string;
    headerTitle: string;
    headerSubtitle: string;
    errorFallback: string;
  }
> = {
  en: {
    greeting: "Hi! I can help you manage appointments.",
    subtitle: "What would you like to do?",
    quickActions: ["Book an appointment", "Reschedule", "Cancel"],
    placeholder: "Type your request...",
    headerTitle: "Scheduling Assistant",
    headerSubtitle: "Book, reschedule, or cancel",
    errorFallback:
      "I'm sorry, something went wrong. Please try again or use the appointment booking page.",
  },
  es: {
    greeting: "\u00a1Hola! Puedo ayudarte a gestionar tus citas.",
    subtitle: "\u00bfQu\u00e9 te gustar\u00eda hacer?",
    quickActions: ["Reservar una cita", "Reprogramar", "Cancelar"],
    placeholder: "Escribe tu solicitud...",
    headerTitle: "Asistente de Citas",
    headerSubtitle: "Reservar, reprogramar o cancelar",
    errorFallback:
      "Lo siento, algo sali\u00f3 mal. Int\u00e9ntalo de nuevo o usa la p\u00e1gina de reservas.",
  },
  vi: {
    greeting: "Xin ch\u00e0o! T\u00f4i c\u00f3 th\u1ec3 gi\u00fap b\u1ea1n qu\u1ea3n l\u00fd l\u1ecbch h\u1eb9n.",
    subtitle: "B\u1ea1n mu\u1ed1n l\u00e0m g\u00ec?",
    quickActions: ["\u0110\u1eb7t l\u1ecbch h\u1eb9n", "\u0110\u1ed5i l\u1ecbch", "H\u1ee7y"],
    placeholder: "Nh\u1eadp y\u00eau c\u1ea7u c\u1ee7a b\u1ea1n...",
    headerTitle: "Tr\u1ee3 l\u00fd L\u1ecbch h\u1eb9n",
    headerSubtitle: "\u0110\u1eb7t, \u0111\u1ed5i ho\u1eb7c h\u1ee7y l\u1ecbch h\u1eb9n",
    errorFallback:
      "Xin l\u1ed7i, \u0111\u00e3 c\u00f3 l\u1ed7i x\u1ea3y ra. Vui l\u00f2ng th\u1eed l\u1ea1i ho\u1eb7c s\u1eed d\u1ee5ng trang \u0111\u1eb7t l\u1ecbch.",
  },
};

// ── General Portal Translations ────────────────────────────────

export const PORTAL_TRANSLATIONS: Record<
  Locale,
  {
    dashboard: string;
    appointments: string;
    messages: string;
    refills: string;
    documents: string;
    invoices: string;
    profile: string;
    logout: string;
    simpleMode: string;
    fullMode: string;
  }
> = {
  en: {
    dashboard: "Dashboard",
    appointments: "Appointments",
    messages: "Messages",
    refills: "Refills",
    documents: "Documents",
    invoices: "Invoices",
    profile: "Profile",
    logout: "Log Out",
    simpleMode: "Simple Mode",
    fullMode: "Full Mode",
  },
  es: {
    dashboard: "Inicio",
    appointments: "Citas",
    messages: "Mensajes",
    refills: "Recetas",
    documents: "Documentos",
    invoices: "Facturas",
    profile: "Perfil",
    logout: "Cerrar sesi\u00f3n",
    simpleMode: "Modo Simple",
    fullMode: "Modo Completo",
  },
  vi: {
    dashboard: "Trang ch\u1ee7",
    appointments: "L\u1ecbch h\u1eb9n",
    messages: "Tin nh\u1eafn",
    refills: "K\u00ea \u0111\u01a1n l\u1ea1i",
    documents: "T\u00e0i li\u1ec7u",
    invoices: "H\u00f3a \u0111\u01a1n",
    profile: "H\u1ed3 s\u01a1",
    logout: "\u0110\u0103ng xu\u1ea5t",
    simpleMode: "Ch\u1ebf \u0111\u1ed9 \u0111\u01a1n gi\u1ea3n",
    fullMode: "Ch\u1ebf \u0111\u1ed9 \u0111\u1ea7y \u0111\u1ee7",
  },
};

/**
 * Detect locale from a text sample.
 * Uses simple heuristics: checks for common Spanish/Vietnamese diacritical marks.
 */
export function detectLocaleFromText(text: string): Locale {
  // Vietnamese-specific characters
  if (/[\u00e0\u00e1\u1ea3\u00e3\u1ea1\u0103\u1eb1\u1eaf\u1eb3\u1eb5\u1eb7\u00e2\u1ea7\u1ea5\u1ea9\u1eab\u1ead\u0111\u00e8\u00e9\u1ebb\u1ebd\u1eb9\u00ea\u1ec1\u1ebf\u1ec3\u1ec5\u1ec7\u00ec\u00ed\u1ec9\u0129\u1ecb\u00f2\u00f3\u1ecf\u00f5\u1ecd\u00f4\u1ed3\u1ed1\u1ed5\u1ed7\u1ed9\u01a1\u1edd\u1edf\u1ee1\u1ee3\u00f9\u00fa\u1ee7\u0169\u1ee5\u01b0\u1eeb\u1ee9\u1eed\u1eef\u1ef1\u1ef3\u00fd\u1ef7\u1ef9\u1ef5]/i.test(text)) {
    return "vi";
  }
  // Spanish common patterns
  if (/[\u00f1\u00bf\u00a1]|(\bhola\b)|(\bcita\b)|(\bpor favor\b)/i.test(text)) {
    return "es";
  }
  return "en";
}

/**
 * Get translations for a given locale with English fallback.
 */
export function getSchedulingTranslations(locale: Locale) {
  return SCHEDULING_AGENT_TRANSLATIONS[locale] || SCHEDULING_AGENT_TRANSLATIONS.en;
}
